/**
 * Primitivos da interface.
 *
 * Todo componente daqui obedece ao sistema de tokens: nenhum hexadecimal
 * solto, nenhuma cor fora de `bg`, `superficie`, `tinta`, `marca` e os seis
 * semânticos. Isso é o que faz os três temas funcionarem sem revisão manual.
 */
import type { ReactNode } from "react";

export type Tom = "ok" | "ember" | "warn" | "crit" | "cool" | "idle" | "marca";

const FUNDO: Record<Tom, string> = {
  ok: "bg-ok-suave text-ok-tinta",
  ember: "bg-ember-suave text-ember-tinta",
  warn: "bg-warn-suave text-warn-tinta",
  crit: "bg-crit-suave text-crit-tinta",
  cool: "bg-cool-suave text-cool-tinta",
  idle: "bg-idle-suave text-idle-tinta",
  marca: "bg-marca-suave text-marca-tinta",
};

/** Selo de estado. Sempre soft + ink: cor cheia sobre suave perde contraste. */
export function Selo({
  children,
  tom = "idle",
  titulo,
}: {
  children: ReactNode;
  tom?: Tom;
  titulo?: string;
}): JSX.Element {
  return (
    <span
      title={titulo}
      className={`inline-block rounded-md px-2 py-0.5 text-[11px] font-medium leading-5 whitespace-nowrap ${FUNDO[tom]}`}
    >
      {children}
    </span>
  );
}

export function Painel({
  titulo,
  dica,
  acoes,
  children,
  semCorpo,
}: {
  titulo?: string;
  dica?: string;
  acoes?: ReactNode;
  children: ReactNode;
  semCorpo?: boolean;
}): JSX.Element {
  return (
    <section className="bg-superficie border border-linha rounded-[10px] overflow-hidden">
      {titulo && (
        <div className="px-5 py-3.5 border-b border-linha flex items-center gap-3 flex-wrap">
          <h2 className="text-[13px] font-semibold tracking-tight">{titulo}</h2>
          {dica && <span className="text-xs text-tinta-3">{dica}</span>}
          {acoes && <div className="ml-auto flex items-center gap-2">{acoes}</div>}
        </div>
      )}
      <div className={semCorpo ? "" : "p-5"}>{children}</div>
    </section>
  );
}

/**
 * ⚠ As classes de tom vêm de um mapa LITERAL, nunca de template string.
 * `text-${tom}` não funciona: o Tailwind varre o código-fonte procurando
 * nomes de classe completos, e uma classe montada em tempo de execução
 * simplesmente não é gerada no CSS. O bug é silencioso — a cor não aparece
 * e nada acusa.
 */
const TINTA: Record<Tom, string> = {
  ok: "text-ok",
  ember: "text-ember",
  warn: "text-warn",
  crit: "text-crit",
  cool: "text-cool",
  idle: "text-idle",
  marca: "text-marca",
};

export function Indicador({
  rotulo,
  valor,
  nota,
  tom,
}: {
  rotulo: string;
  valor: string | number;
  nota?: string;
  tom?: Tom;
}): JSX.Element {
  return (
    <div className="bg-superficie border border-linha rounded-[10px] px-4 py-3.5">
      <div className="text-[10.5px] uppercase tracking-[0.08em] text-tinta-3 font-semibold">
        {rotulo}
      </div>
      <div
        className={`text-[26px] leading-8 font-semibold tabular mt-1 tracking-tight ${
          tom ? TINTA[tom] : ""
        }`}
      >
        {valor}
      </div>
      {nota && <div className="text-[11.5px] text-tinta-2 mt-0.5">{nota}</div>}
    </div>
  );
}

/**
 * Aviso. Existe para o painel não mentir por omissão: quando não há número
 * apto, dizer isso é mais importante do que mostrar um zero elegante.
 */
export function Aviso({
  children,
  tom = "warn",
}: {
  children: ReactNode;
  tom?: Tom;
}): JSX.Element {
  return (
    <div
      role="status"
      className={`rounded-[10px] px-4 py-3 text-[13px] leading-relaxed ${FUNDO[tom]}`}
    >
      {children}
    </div>
  );
}

export function Botao({
  children,
  onClick,
  tipo = "normal",
  submit,
  desabilitado,
  titulo,
  pequeno,
}: {
  children: ReactNode;
  onClick?: () => void;
  tipo?: "normal" | "principal" | "fantasma" | "perigo";
  submit?: boolean;
  desabilitado?: boolean;
  titulo?: string;
  pequeno?: boolean;
}): JSX.Element {
  const base =
    "inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const tam = pequeno ? "text-xs px-2.5 py-1.5" : "text-[13px] px-3.5 py-2";
  const estilo = {
    normal: "bg-superficie-3 text-tinta hover:bg-linha",
    principal: "bg-marca text-superficie hover:opacity-90",
    fantasma: "text-tinta-2 hover:text-tinta hover:bg-superficie-3",
    perigo: "bg-crit-suave text-crit-tinta hover:opacity-85",
  }[tipo];
  return (
    <button
      type={submit ? "submit" : "button"}
      onClick={onClick}
      disabled={desabilitado}
      title={titulo}
      className={`${base} ${tam} ${estilo}`}
    >
      {children}
    </button>
  );
}

export function Campo({
  rotulo,
  children,
  dica,
}: {
  rotulo: string;
  children: ReactNode;
  dica?: string;
}): JSX.Element {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-tinta-2 mb-1.5">
        {rotulo}
      </span>
      {children}
      {dica && <span className="block text-[11px] text-tinta-3 mt-1">{dica}</span>}
    </label>
  );
}

export const classeEntrada =
  "w-full rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[13px] text-tinta outline-none focus:border-marca placeholder:text-tinta-3";

export function Tabela({
  colunas,
  children,
  larguraMin = 720,
}: {
  colunas: Array<{ chave: string; rotulo: string; direita?: boolean }>;
  children: ReactNode;
  larguraMin?: number;
}): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left" style={{ minWidth: larguraMin }}>
        <thead>
          <tr className="text-[10.5px] uppercase tracking-[0.08em] text-tinta-3">
            {colunas.map((c) => (
              <th
                key={c.chave}
                className={`font-semibold px-5 py-2.5 ${c.direita ? "text-right" : ""}`}
              >
                {c.rotulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Vazio({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="py-12 text-center text-[13px] text-tinta-3">{children}</div>
  );
}

export function Carregando(): JSX.Element {
  return (
    <div className="py-12 text-center text-[13px] text-tinta-3" role="status">
      Carregando…
    </div>
  );
}

/** Barra de progresso de etapa. */
export function Medidor({ pct }: { pct: number }): JSX.Element {
  const v = Math.max(0, Math.min(100, pct));
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-full max-w-[120px] rounded-full bg-superficie-3 overflow-hidden">
        <div
          className={`h-full rounded-full ${v === 100 ? "bg-ok" : "bg-marca"}`}
          style={{ width: `${v}%` }}
        />
      </div>
      <span className="text-[11px] text-tinta-3 tabular">{v}%</span>
    </div>
  );
}
